import { LuIcon, Text } from "@overmap-ai/blocks"
import type { ProcedureStep, Stored } from "@overmap-ai/common"
import { type ComponentPropsWithRef, memo, useCallback, useMemo } from "react"
import {
	Bar,
	BarChart,
	type BarProps,
	BarStack,
	LabelList,
	type LabelProps,
	ResponsiveContainer,
	Text as ChartText,
	Tooltip,
	type TooltipContentProps,
	XAxis,
	YAxis,
} from "recharts"

import { PROCEDURE_STEP_COMPLETION_STATUSES } from "../../constants"
import { modelIconMapping } from "../../mappings/icons"
import { procedureStepCompletionStatusData } from "../../mappings/procedures"
import type { ProcedureStepCompletionData } from "../../typings"
import { TooltipWrapper } from "./components/TooltipWrapper"
import { ZERO_MARGIN } from "./constants"

interface ProcedureStepStackedBarChartProps extends Omit<
	ComponentPropsWithRef<typeof ResponsiveContainer>,
	"children"
> {
	procedureSteps: Stored<ProcedureStep>[]
	procedureStepCompletionDatas: Record<ProcedureStep["uuid"], ProcedureStepCompletionData>
	onProcedureStepClick?: (procedureStepId: ProcedureStep["uuid"]) => void
	hideTooltip?: boolean
}

export const ProcedureStepStackedBarChart = memo<ProcedureStepStackedBarChartProps>((props) => {
	const { procedureSteps, procedureStepCompletionDatas, onProcedureStepClick, hideTooltip, ...rest } = props

	const data = useMemo(() => {
		return procedureSteps.map((procedureStep) => {
			const stepData = procedureStepCompletionDatas[procedureStep.uuid]

			return {
				name: procedureStep.name,
				color: procedureStep.color,
				uuid: procedureStep.uuid,
				...stepData,
			}
		})
	}, [procedureStepCompletionDatas, procedureSteps])

	const handleBarClick: NonNullable<BarProps["onClick"]> = useCallback(
		(barRectangleItem, _index, _e) => {
			onProcedureStepClick?.(barRectangleItem.payload.uuid)
		},
		[onProcedureStepClick],
	)

	return (
		<ResponsiveContainer minHeight={procedureSteps.length * 24} {...rest}>
			<BarChart
				data={data}
				className="text-xs"
				layout="vertical"
				barSize={24}
				barCategoryGap="20%"
				margin={ZERO_MARGIN}
			>
				<XAxis type="number" hide domain={[0, "dataMax"]} />
				<YAxis type="category" dataKey="name" hide />
				<BarStack radius={4}>
					{PROCEDURE_STEP_COMPLETION_STATUSES.toReversed().map((status) => {
						const { label, color } = procedureStepCompletionStatusData[status]

						return (
							<Bar
								key={status}
								dataKey={status}
								name={label}
								fill={color}
								onClick={handleBarClick}
								isAnimationActive={false}
								cursor={onProcedureStepClick ? "pointer" : undefined}
							>
								<LabelList dataKey="name" content={LabelListContent} />
							</Bar>
						)
					})}
				</BarStack>

				{!hideTooltip && <Tooltip content={TooltipContent} cursor={false} />}
			</BarChart>
		</ResponsiveContainer>
	)
})
ProcedureStepStackedBarChart.displayName = "ProcedureStepStackedBarChart"

function TooltipContent(props: TooltipContentProps<number, string>) {
	const { active, payload, label } = props

	const isVisible = active && payload && payload.length

	if (!isVisible) return null

	return (
		<TooltipWrapper className="flex flex-col gap-2" size="xs">
			<Text size="sm" weight="medium">
				{label}
			</Text>
			<ol className="flex flex-col gap-1">
				{payload.map(({ dataKey, value, color, name }) => {
					return (
						<li key={dataKey} className="flex items-center gap-1">
							<LuIcon icon={modelIconMapping.stage} color={color} fill={color} />
							<div className="flex grow items-center justify-between gap-2">
								<Text accentColor="base" size="sm">
									{name ?? dataKey}
								</Text>
								<Text size="sm">{value}</Text>
							</div>
						</li>
					)
				})}
			</ol>
		</TooltipWrapper>
	)
}

function LabelListContent(props: LabelProps) {
	const { y, height, value } = props

	if (typeof y !== "number" || typeof height !== "number") return null

	return (
		<ChartText
			x={10}
			y={y + height / 2}
			dominantBaseline="central"
			className="fill-(--base-12)"
			fontWeight={500}
			fontSize={12}
			pointerEvents="none"
		>
			{value}
		</ChartText>
	)
}
